import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const TIMEOUT_MS = 20000;

const DISTRICT_TYPES = ["district"];
const CITY_TYPES = ["city", "locality"];

function geocoderKey() {
  if (typeof document === "undefined") {
    return "";
  }
  return document.documentElement.dataset.geocoderKey || "";
}

function isFiniteNumber(v) {
  return typeof v === "number" && isFinite(v);
}

function validLatLon(lat, lon) {
  return isFiniteNumber(lat) && isFiniteNumber(lon)
    && lat >= -90 && lat <= 90
    && lon >= -180 && lon <= 180;
}

function str(v) {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

function cancelledError() {
  const err = new Error("geocoder request cancelled");
  err.cancelled = true;
  return err;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this.requestSeq = 0;
  }

  cancelAutocomplete() {
    this.requestSeq++;
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (p.length === 0 && g.name) {
      p.push(g.name);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _geoaddress(l) {
    if (!l || !l.properties || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    const lng = l.geometry.coordinates[0];
    const lat = l.geometry.coordinates[1];
    if (!validLatLon(lat, lng)) {
      return null;
    }

    const p = l.properties;
    const type = str(p.type);
    const name = str(p.name);

    let street = str(p.street);
    let city = str(p.city);
    let district = str(p.district);

    if (type) {
      if (type === "street" && !street) {
        street = name;
      }
      if (CITY_TYPES.indexOf(type) !== -1 && !city) {
        city = name;
      }
      if (DISTRICT_TYPES.indexOf(type) !== -1 && !district && name !== city) {
        district = name;
      }
    } else {
      // Legacy-Fallback für Antworten ohne properties.type
      if (p.osm_key == "highway" && !street) {
        street = name;
      }
      if (!district && name && name !== city) {
        if (p.osm_key == "boundary" || (p.osm_key == "place" && p.osm_value == "suburb")) {
          district = name;
        }
      }
      if (!city && !street && p.osm_key == "place" && name) {
        city = name;
      }
    }

    const geoaddress = {};

    if (street) {
      geoaddress["street"] = street;
      if (str(p.housenumber)) {
        geoaddress["number"] = p.housenumber;
      }
    }

    if (str(p.postcode)) {
      geoaddress["zip"] = p.postcode;
    }
    if (city) {
      geoaddress["city"] = city;
    }
    if (district && district !== city) {
      geoaddress["district"] = district;
    }
    if (name) {
      geoaddress["name"] = name;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = p.state;
    geoaddress["country"] = p.country;

    geoaddress["text"] = this._geoaddressText(geoaddress);

    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  _features(res) {
    if (res && res.data && Array.isArray(res.data.features)) {
      return res.data.features;
    }
    return [];
  }

  autocomplete(q) {
    this.cancelAutocomplete();

    const key = geocoderKey();
    if (!q || !key) {
      return Promise.resolve([]);
    }

    const seq = this.requestSeq;
    const source = this.axios.CancelToken.source();
    const request = { source };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("key", key);

    return this.axios.get(SEARCH_URL, {
      cancelToken: source.token,
      timeout: TIMEOUT_MS,
      params
    }).then(res => {
      if (seq !== this.requestSeq) {
        throw cancelledError();
      }

      const results = [];
      this._features(res).forEach(l => {
        const geoaddress = this._geoaddress(l);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });

      return results;
    }, thrown => {
      if (this.axios.isCancel(thrown) || seq !== this.requestSeq) {
        throw cancelledError();
      }
      throw thrown;
    }).finally(() => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    });
  }

  reverse(lat, lng) {
    const key = geocoderKey();
    lat = Number(lat);
    lng = Number(lng);

    if (!key || !validLatLon(lat, lng)) {
      return Promise.resolve(null);
    }

    return this.axios.get(REVERSE_URL, {
      timeout: TIMEOUT_MS,
      params: {
        lat: lat,
        lon: lng,
        lang: "de",
        key: key
      }
    }).then(res => {
      const features = this._features(res);
      for (let i = 0; i < features.length; i++) {
        const geoaddress = this._geoaddress(features[i]);
        if (geoaddress) {
          return geoaddress;
        }
      }
      return null;
    }, () => null);
  }
}

function GeocoderFactory() {
  return depsloader.load("axios").then(axios => new Geocoder(axios));
}

export { GeocoderFactory };

export default {};
